import { Fragment, type KeyboardEventHandler } from 'react';
import type { LeaveType } from '@aegislab/shared';
import { formatDurationKo } from '../../../lib/format';
import { selectParticle } from '../../../lib/getParticle';
import { LEAVE_LABELS } from './calendarHeatmapLabels';
import { scheduleSpanToneClass } from './calendarHeatmapTones';
import * as s from './CalendarHeatmapCell.css';

/** 기간 줄 간격 — 선끼리 붙지 않을 만큼만. 이웃 줄 이름이 부딪히면 이름을 옆으로 민다(labelOffset) */
const SPAN_PITCH = 7;
export interface CalendarOthersOnLeaveEntry {
  userId: string;
  userName: string;
  leaveType: LeaveType;
  isMyTeam: boolean;
}

export interface CalendarHeatmapCellProps {
  date: string;
  hoursWorked: number;
  workMs?: number;
  isAbsent?: boolean;
  tabIndex?: number;
  onFocus?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  isToday?: boolean;
  isWeekend?: boolean;
  leave?: { type: LeaveType };
  correction?: { status: 'PENDING' | 'APPROVED' };
  offDayRequest?: { id: string; status: 'PENDING' | 'APPROVED' | 'REJECTED' };
  othersOnLeave?: CalendarOthersOnLeaveEntry[];
  /**
   * 그날 적어 둔 개인 일정이 있는지. 있음/없음만 나른다 — 셀에는 개수 배지나 제목이 앉을
   * 자리가 없다(날짜 숫자가 컨테이너 폭을 따라 커지고 휴가자 이름이 이미 두 줄을 먹는다).
   */
  hasSchedule?: boolean;
  /** 공휴일·회사 지정 휴무일. 주말과 다른 색으로 숫자를 그리고 아래에 이름을 붙인다 */
  holiday?: { name: string };
  /**
   * 월급일. 공휴일처럼 날짜의 성질이라 칸 안 이름표로 그린다 — 숫자 색은 그대로다.
   * 앞당겼으면 원래 날짜와 이유(cause: 요일·공휴일 이름)를 접근 이름에 싣는다
   */
  payday?: { shiftedFrom?: string; reason?: 'weekend' | 'holiday'; cause?: string };
  /** 그날 팀 마감 수(퀘스트 마감 + 캠페인 일정). 0·미전달이면 배지를 그리지 않는다 */
  deadlineCount?: number;
  /** 마감 2건 이상인데 휴가자가 있는 날 — 칸 테두리로 강조한다 */
  crunch?: boolean;
  /**
   * 여러 날에 걸친 일정이 이 날을 지나는 자리 — 칸 아래쪽에 줄(lane)마다 4px 선을 쌓는다. 같은 일정은
   * 기간 내내 같은 줄·같은 색(tone)이라 이웃 칸의 선과 이어져 한 기간으로 읽힌다. 이름은 날짜 상세가 든다.
   */
  scheduleSpans?: {
    lane: number;
    edge: 'start' | 'middle' | 'end' | 'single';
    tone: number;
    /** 일정 이름 — 기간이 시작하는 칸과 주가 바뀐 첫 칸에만 */
    label?: string;
    /** 이름이 뻗어도 되는 칸 수(그 주 안에서 기간이 이어지는 칸) */
    labelDays?: number;
    /** 이웃 줄 이름과 부딪힐 때 옆으로 미는 거리(px). 음수면 왼쪽 */
    labelOffset?: number;
  }[];
  /**
   * 칸이 낮아 휴가자 이름 줄까지 담지 못한다 — 이름 대신 인원 배지를 숫자 옆에 둔다(데스크톱).
   * 날짜 숫자와 월급일 이름표는 그대로 남고, 휴가자는 칸 이름·날짜 상세에 남는다. 판정은 달력이 한다
   */
  othersFolded?: boolean;
  /** 휴가자 상세 영역이 이 셀 기준으로 열려 있는지 */
  othersExpanded?: boolean;
  /** 휴가자 상세 영역 요소 id (aria-controls) */
  othersDetailId?: string;
  /** 전달되고 휴가자가 있으면 셀이 버튼으로 렌더된다 */
  onToggleOthers?: () => void;
  /** 전달 시 셀이 button으로 렌더되고 클릭하면 호출된다 (범용 날짜 선택) */
  onSelectDate?: () => void;
}

const PAYDAY_REASON_LABELS = { weekend: '주말', holiday: '공휴일' } as const;

/**
 * `월급일` · 앞당겼으면 `월급일 (25일이 일요일이어서 앞당김)` — 이유는 요일·공휴일 이름(cause)이고,
 * 없으면 주말·공휴일로 적는다. 원래 날짜가 다른 달이면 월까지 적는다
 */
function paydayLabel(date: string, payday: NonNullable<CalendarHeatmapCellProps['payday']>) {
  if (!payday.shiftedFrom || !payday.reason) return '월급일';
  const day = Number(payday.shiftedFrom.slice(8, 10));
  const scheduled =
    payday.shiftedFrom.slice(0, 7) === date.slice(0, 7)
      ? `${day}일`
      : `${Number(payday.shiftedFrom.slice(5, 7))}월 ${day}일`;
  const cause = payday.cause ?? PAYDAY_REASON_LABELS[payday.reason];
  return `월급일 (${scheduled}이 ${cause}${selectParticle(cause, '이어서', '여서')} 앞당김)`;
}

const CORRECTION_LABELS: Record<string, string> = {
  PENDING: '정정대기',
  APPROVED: '정정승인',
};

function computeOpacity(hours: number): number {
  if (hours <= 0) return 0;
  const ratio = hours / s.STANDARD_HOURS;
  return Math.min(ratio * 0.65, 0.75);
}

const OFF_DAY_LABELS: Record<string, string> = {
  PENDING: '비근무일 요청 대기',
  APPROVED: '비근무일 요청 승인',
  REJECTED: '비근무일 요청 반려',
};

export function CalendarHeatmapCell({
  date,
  hoursWorked,
  workMs,
  isAbsent = false,
  tabIndex,
  onFocus,
  onKeyDown,
  isToday = false,
  isWeekend = false,
  leave,
  correction,
  offDayRequest,
  othersOnLeave,
  hasSchedule = false,
  holiday,
  payday,
  deadlineCount = 0,
  crunch = false,
  scheduleSpans = [],
  othersFolded = false,
  othersExpanded = false,
  othersDetailId,
  onToggleOthers,
  onSelectDate,
}: CalendarHeatmapCellProps) {
  const dayNumber = String(Number(date.slice(8, 10)));
  const opacity = computeOpacity(hoursWorked);

  const othersCount = othersOnLeave?.length ?? 0;
  const teamCount = othersOnLeave?.filter((e) => e.isMyTeam).length ?? 0;
  const folded = othersFolded && othersCount > 0;
  // 월급일 이름표가 숫자를 위로 올린 모바일 칸에서는 왼쪽 위 배지가 숫자를 덮어 숫자 옆으로 옮긴다
  const paydayBadgeInline = othersCount > 0 && !!payday;
  const othersBadgeVariant = s.othersBadgeVariants[teamCount > 0 ? 'team' : 'company'];
  const othersSummary =
    othersCount > 0
      ? `휴가자 ${othersCount}명${teamCount > 0 ? ` (우리팀 ${teamCount}명)` : ''}`
      : null;

  const cellClasses = [
    s.cell,
    isToday ? s.todayCell : '',
    isWeekend ? s.weekendText : '',
    holiday ? s.holidayText : '',
    othersCount > 0 || holiday || payday ? s.cellWithNames : '',
    crunch ? s.crunchCell : '',
    scheduleSpans.some((x) => x.label) ? s.cellLabelOverflow : '',
  ]
    .filter(Boolean)
    .join(' ');

  const durationMs = workMs ?? hoursWorked * 3_600_000;
  const attendanceLabel = isAbsent
    ? '출퇴근 기록 없음 · 결근'
    : durationMs >= 60_000
      ? `${formatDurationKo(durationMs)} 근무`
      : '근무 없음';
  const labelParts = [`${date}: ${attendanceLabel}`];
  if (holiday) labelParts.push(`공휴일 ${holiday.name}`);
  if (payday) labelParts.push(paydayLabel(date, payday));
  if (leave) labelParts.push(LEAVE_LABELS[leave.type] ?? leave.type);
  if (correction) labelParts.push(CORRECTION_LABELS[correction.status] ?? correction.status);
  if (offDayRequest) labelParts.push(OFF_DAY_LABELS[offDayRequest.status] ?? '비근무일 요청');
  if (othersSummary) labelParts.push(othersSummary);
  // 점만으로는 스크린리더가 아무것도 못 읽는다 — 이름에 실어야 장식이 아닌 신호가 된다
  if (hasSchedule) labelParts.push('일정 있음');
  if (deadlineCount > 0) labelParts.push(`마감 ${deadlineCount}건`);
  if (crunch) labelParts.push('마감이 몰린 날에 휴가자가 있습니다');
  if (scheduleSpans.length > 0) labelParts.push(`기간 일정 ${scheduleSpans.length}건`);

  const titleParts = [`${date}: ${hoursWorked}h`];
  if (holiday) titleParts.push(`공휴일 ${holiday.name}`);
  if (payday) titleParts.push(paydayLabel(date, payday));
  if (leave) titleParts.push(LEAVE_LABELS[leave.type] ?? leave.type);
  if (correction) titleParts.push(CORRECTION_LABELS[correction.status] ?? correction.status);
  if (offDayRequest) titleParts.push(OFF_DAY_LABELS[offDayRequest.status] ?? '비근무일 요청');
  if (othersSummary) titleParts.push(othersSummary);
  if (hasSchedule) titleParts.push('일정 있음');
  if (deadlineCount > 0) titleParts.push(`마감 ${deadlineCount}건`);

  const backgroundColor = opacity > 0 ? `rgba(34, 197, 94, ${String(opacity)})` : undefined;

  const content = (
    <>
      {folded || paydayBadgeInline ? (
        <span className={s.dayNumberRow}>
          {dayNumber}
          <span
            className={`${s.othersBadgeInline} ${folded ? s.othersBadgeInlineDesktop : ''} ${paydayBadgeInline ? s.othersBadgeInlineMobile : ''} ${othersBadgeVariant}`}
            aria-hidden
            data-others-inline-badge
          >
            {othersCount}
          </span>
        </span>
      ) : (
        dayNumber
      )}
      {holiday && (
        <span className={s.holidayName} aria-hidden data-holiday-name>
          {holiday.name}
        </span>
      )}
      {payday && (
        <span className={s.paydayName} aria-hidden data-payday-name>
          월급일
        </span>
      )}
      {leave && (
        <span className={`${s.leaveStripe} ${s.leaveStripeVariants[leave.type]}`} aria-hidden />
      )}
      {correction && (
        <span
          className={`${s.correctionDot} ${s.correctionDotVariants[correction.status]}`}
          aria-hidden
        />
      )}
      {offDayRequest && (
        <span
          className={`${s.offDayDot} ${s.offDayDotVariants[offDayRequest.status]}`}
          aria-hidden
          data-offday-dot
        />
      )}
      {hasSchedule && scheduleSpans.length === 0 && (
        <span className={s.scheduleDot} aria-hidden data-schedule-dot />
      )}
      {scheduleSpans.map((span) => (
        <Fragment key={span.lane}>
          <span
            className={`${s.spanLine} ${s.spanLineEdges[span.edge]} ${scheduleSpanToneClass(span.tone)}`}
            style={{ bottom: 3 + span.lane * SPAN_PITCH }}
            aria-hidden
          />
          {span.label && (
            <span
              className={`${s.spanLabel} ${scheduleSpanToneClass(span.tone)}`}
              style={{
                // 4px 선 한가운데에 겹쳐 앉고, 그리는 순서는 선보다 위다(spanLabel zIndex)
                bottom: 3 + span.lane * SPAN_PITCH - 4,
                left: 4 + (span.labelOffset ?? 0),
                maxWidth: `calc(${span.labelDays ?? 1} * 100% + ${(span.labelDays ?? 1) - 1}px - 8px)`,
              }}
              aria-hidden
            >
              {span.label}
            </span>
          )}
        </Fragment>
      ))}
      {deadlineCount > 0 && (
        <span className={s.deadlineBadge} aria-hidden data-deadline-badge>
          {deadlineCount}
        </span>
      )}
      {othersCount > 0 && (
        <>
          {!paydayBadgeInline && (
            <span className={`${s.othersBadge} ${othersBadgeVariant}`} aria-hidden>
              {othersCount}
            </span>
          )}
          {/* 최대 2줄: 2명 이하 전원 이름, 3명 이상 1명 + "+N". 접었으면 숫자 옆 배지가 대신한다 */}
          {!folded && (
            <span className={s.othersNames} aria-hidden>
              {othersOnLeave?.slice(0, othersCount <= 2 ? 2 : 1).map((entry) => (
                <span
                  key={`${entry.userId}-${entry.leaveType}`}
                  className={`${s.othersName} ${entry.isMyTeam ? s.othersNameTeam : ''}`}
                >
                  {entry.userName}
                </span>
              ))}
              {othersCount > 2 && <span className={s.othersName}>+{othersCount - 1}</span>}
            </span>
          )}
        </>
      )}
    </>
  );

  if (onSelectDate) {
    return (
      <button
        type="button"
        className={`${cellClasses} ${s.cellButton}`}
        style={{ backgroundColor }}
        aria-label={labelParts.join(', ')}
        title={titleParts.join(' | ')}
        onClick={onSelectDate}
        data-calendar-date={date}
        tabIndex={tabIndex}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      >
        {content}
      </button>
    );
  }

  if (othersCount > 0 && onToggleOthers) {
    return (
      <button
        type="button"
        className={`${cellClasses} ${s.cellButton}`}
        style={{ backgroundColor }}
        aria-label={labelParts.join(', ')}
        aria-expanded={othersExpanded}
        aria-controls={othersDetailId}
        title={titleParts.join(' | ')}
        onClick={onToggleOthers}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={cellClasses}
      style={{ backgroundColor }}
      aria-label={labelParts.join(', ')}
      title={titleParts.join(' | ')}
    >
      {content}
    </div>
  );
}
